/**
 * A drive between two jobs, detected. Pure, no React.
 *
 * The worker calendar and the disposition board both draw every appointment/task as
 * `Anfahrt → Arbeit → Abfahrt`. When the previous job left no Abfahrt planned, the technician was
 * never assumed to head back to base in between — the next job's Anfahrt is really a straight drive
 * from the last site, a **Transfer**, not a fresh trip out. Symmetrically, a job's own Abfahrt is
 * a Transfer when the *next* job has no Anfahrt of its own.
 *
 * Deliberately blind to gaps: idle time between two jobs does not cancel the detection, since the
 * technician still isn't assumed to have gone back to base — they simply had slack before the next
 * drive. Deliberately blind to BREAK/ABSENCE too: a Pause between two jobs does not send anyone back
 * to the Werkhof, so it is skipped when looking for the neighbour on either side.
 */

export type TravelKind = 'Anfahrt' | 'Abfahrt' | 'Transfer';

export interface TransferInput {
  /** Stable block identity — the calendar's `${kind}:${id}`, or the board's block key. */
  key: string;
  /** Only APPOINTMENT and TASK take part; BREAK and ABSENCE are passed with `isJob: false`. */
  isJob: boolean;
  /** Any consistent chronological key — day-relative minutes or an epoch ms both work. */
  startMin: number;
  travelMinutes: number;
  departureMinutes: number;
}

export interface TransferSets {
  /** Keys whose Anfahrt is a drive straight from the previous job. */
  travel: Set<string>;
  /** Keys whose Abfahrt is a drive straight to the next job. */
  departure: Set<string>;
}

export function detectTransfers(blocks: TransferInput[]): TransferSets {
  const travel = new Set<string>();
  const departure = new Set<string>();

  const jobs = blocks.filter((block) => block.isJob).sort((a, b) => a.startMin - b.startMin);

  for (let i = 0; i < jobs.length - 1; i++) {
    const curr = jobs[i];
    const next = jobs[i + 1];

    if (next.travelMinutes > 0 && curr.departureMinutes === 0) travel.add(next.key);
    if (curr.departureMinutes > 0 && next.travelMinutes === 0) departure.add(curr.key);
  }

  return { travel, departure };
}
